import {Observable, ReplaySubject, Subscription} from 'rxjs';
import {streamGameAnalysis} from './streamGameAnalysisApi';
import type {BrowserAnalysisMode} from './browserGameAnalysis';

/**
 * Keeps a game's in-browser analysis (Web Worker pool + SSE fallback) running
 * independent of any ChessAnalysis screen instance. `streamGameAnalysis` is a
 * cold Observable - every subscribe starts a fresh engine/pool and every
 * unsubscribe tears it down via its own cleanup. Without this layer,
 * navigating away from the screen unsubscribes the UI's listener and kills
 * the engine mid-analysis. Here, the manager owns one long-lived subscription
 * per (gameId, mode) and the UI subscribes to a ReplaySubject that mirrors
 * it - unmounting the screen only detaches the UI listener, so the analysis
 * keeps running in the background until it finishes or is explicitly stopped.
 */
type AnalysisResponse = any;

interface ActiveAnalysis {
  subject: ReplaySubject<AnalysisResponse>;
  subscription: Subscription;
}

const activeAnalyses = new Map<string, ActiveAnalysis>();

function makeKey(gameId: number, mode: BrowserAnalysisMode): string {
  return `${gameId}:${mode}`;
}

export function getBackgroundGameAnalysis(
  gameId: number,
  mode: BrowserAnalysisMode,
  alreadyAnalyzed: boolean,
): Observable<AnalysisResponse> {
  const key = makeKey(gameId, mode);
  let entry = activeAnalyses.get(key);

  if (!entry) {
    const subject = new ReplaySubject<AnalysisResponse>();
    const subscription = streamGameAnalysis(
      gameId,
      mode,
      alreadyAnalyzed,
    ).subscribe({
      next: value => subject.next(value),
      error: err => {
        subject.error(err);
        activeAnalyses.delete(key);
      },
      complete: () => {
        subject.complete();
        activeAnalyses.delete(key);
      },
    });
    entry = {subject, subscription};
    activeAnalyses.set(key, entry);
  }

  return entry.subject.asObservable();
}

/** Actually tears down the engine/worker for a game - used for an explicit user stop/restart, not for screen navigation. */
export function stopBackgroundGameAnalysis(
  gameId: number,
  mode: BrowserAnalysisMode,
): void {
  const key = makeKey(gameId, mode);
  const entry = activeAnalyses.get(key);
  if (entry) {
    entry.subscription.unsubscribe();
    activeAnalyses.delete(key);
  }
}
